'use client';
import React, { useMemo, useState } from 'react';
import { HorizontalSplit } from '@/components/HorizontalSplit';
import { PageInfo } from '@/app/(auth)/signup/components/PageInfo';
import { Input } from '@/components/Input';
import { ErrorMessage } from '@/components/ErrorMessage';
import { useMutation } from '@tanstack/react-query';
import { isRouteErrorResponse } from '@/utils/isRouteErrorResponse';
import {
  ACCOUNT_NOT_ACTIVATED,
  LoginRedirectReason,
  loginResultSchema,
} from '@/interfaces/auth/ILoginResult';
import { logrocketService } from '@/services/logrocket';
import { analyticsService } from '@/services/analytics';
import { LoadingButton } from '@/components/LoadingButton';
import Lottie from 'lottie-react';
import buildingsAnimation from '@/assets/animations/buildings.json';
import Image from 'next/image';
import bruntwoodImg from '@/assets/bruntwood.png';
import bruntwoodPhoto from '@/assets/images/bruntwood-photo2.jpeg';
import { setWindowHref } from '@/utils/setWindowHref';

export function Login({
  searchParams,
  myAccountUrl,
}: {
  searchParams: { [key: string]: string | string[] | undefined };
  myAccountUrl: string;
}) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const emailIsValid = useMemo(() => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(username);
  }, [username]);

  // A latch, set when we're redirecting so that we can keep showing the spinner until the next page loads.
  // Carries the redirect's reason so the interstitial can say why - derived, not separate state,
  // so there's nothing to reset between results
  const [redirect, setRedirect] = useState<{ reason?: LoginRedirectReason } | null>(null);
  const isRedirecting = redirect !== null;
  const redirectHeadline =
    redirect?.reason === ACCOUNT_NOT_ACTIVATED
      ? 'Your account isn’t activated yet — taking you back to finish signing up'
      : 'Building your dashboard';

  const submitMutation = useMutation({
    mutationKey: ['login', { username, password }],
    mutationFn: async () => {
      const response = await fetch(`${process.env.NEXT_PUBLIC_AUTH_BASE_URL}/login/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password, redirect: searchParams['redirect'] }),
      });
      const data = await response.json();
      if (!response.ok) {
        if (isRouteErrorResponse(data)) {
          const error = new Error(data.error);
          analyticsService.loginFail({ label: 'login failed', value: data.error });
          logrocketService.logException(error);
          throw error;
        } else {
          const error = new Error('An unknown error occurred');
          analyticsService.loginFail({ label: 'login failed - unknown error', value: error.message });
          logrocketService.logException(error);
          throw error;
        }
      }

      const result = loginResultSchema.parse(data);

      if (result.redirectUrl) {
        setRedirect({ reason: result.reason });
        if (result.reason === ACCOUNT_NOT_ACTIVATED) {
          // Not a login: the account was never activated and we're sending them back into
          // signup to finish. The interstitial headline says so (see redirectHeadline)
          analyticsService.loginFail({ label: 'login failed - account not activated', value: username });
        } else {
          analyticsService.login({ label: 'login success' });
        }
        // Just use window.location.href to redirect the user rather than the router.push, which can do weird things when working between services.
        setWindowHref(result.redirectUrl);
      }

      if (result.outcome === 'success' || result.outcome === 'redirect') return result;

      const error = new Error(result.message);
      analyticsService.loginFail({ label: 'login failed', value: error.message });
      logrocketService.logException(error);
      throw error;
    },
  });

  const isLoading = isRedirecting || submitMutation.isPending;

  if (isRedirecting) {
    return (
      <>
        <header className="tw:flex tw:justify-between tw:items-center tw:h-0 tw:md:h-16 tw:lg:h-20 tw:bg-white tw:md:px-4 tw:invisible tw:md:visible">
          <div className="tw:h-7 tw:lg:h-12 tw:relative tw:md:pl-11">
            <a href="https://bruntwood.com"></a>
            <Image src={bruntwoodImg} alt="Bruntwood" className="tw:h-full tw:w-min" />
          </div>
        </header>
        <HorizontalSplit>
          <PageInfo headline={redirectHeadline} />
          <div className="tw:flex tw:flex-col tw:items-center tw:justify-center tw:h-full">
            <div className="">
              <Lottie animationData={buildingsAnimation} loop autoplay />
            </div>
          </div>
        </HorizontalSplit>
      </>
    );
  }

  return (
    <div className="tw:flex tw:items-stretch tw:justify-between tw:p-4 tw:sm:p-10 tw:lg:p-0 tw:lg:items-stretch tw:lg:justify-between tw:h-full tw:flex-col tw:lg:flex-row">
      <div className="tw:flex-1 tw:p-4 tw:flex tw:justify-center tw:items-center tw:min-h-screen tw:lg:min-h-0">
        <form
          className="tw:group tw:w-full tw:md:w-3/4"
          onSubmit={e => {
            e.preventDefault();
            submitMutation.mutate();
          }}
          noValidate>
          <div className="tw:flex tw:flex-col tw:gap-2 tw:w-full">
            <div className="tw:flex tw:flex-col tw:items-center tw:text-center tw:gap-2 tw:mb-4">
              <a href={myAccountUrl}>
                <Image
                  src={bruntwoodImg}
                  alt="Bruntwood-logo"
                  className="tw:cursor-pointer tw:w-auto tw:h-12 tw:lg:h-18 tw:mb-8"
                />
              </a>
              <h3 className="tw:text-3xl tw:lg:mb-4 tw:font-medium">Welcome back!</h3>
              <p className="tw:text-sm tw:sm:text-lg tw:font-extralight">
                Enter your credentials to access your account
              </p>
            </div>
            <Input
              key="username"
              name="username"
              id="username"
              label="Username"
              type="text"
              value={username}
              onChange={value => setUsername(value.trim())}
              required
              errorMessage={!emailIsValid && username ? 'Please enter a valid email address' : undefined}
            />
            <Input
              id="password"
              name="password"
              label="Password"
              type="password"
              value={password}
              onChange={setPassword}
              required
              showPasswordToggle
              hasError={!!submitMutation.error}
            />

            {submitMutation.error && <ErrorMessage error={submitMutation.error.message} />}

            <LoadingButton
              size="md"
              id="submit"
              type="submit"
              isLoading={isLoading}
              className="tw:bg-primary tw:text-white tw:w-full tw:rounded-full tw:py-4 tw:my-6 tw:group-invalid:pointer-events-none tw:group-invalid:opacity-30 tw:flex tw:justify-center tw:items-center tw:font-medium tw:text-lg tw:transition tw:duration-150 tw:ease-in-out"
              label="Login"
              variant="primary"
            />
            <div className="tw:flex-col tw:lg:flex-row tw:flex tw:justify-between tw:gap-4 tw:flex-wrap">
              <a
                className="tw:text-primary tw:underline tw:decoration-2 tw:underline-offset-8 tw:font-medium"
                href="/auth/forgot-password/request-email">
                Forgot Password
              </a>
              <p className="tw:text-sm tw:sm:text-lg tw:font-extralight">
                Not signed up yet?
                <a
                  className="tw:text-primary tw:underline tw:decoration-2 tw:underline-offset-8 tw:pl-2 tw:font-medium"
                  href="/auth/signup">
                  Start here
                </a>
              </p>
            </div>
          </div>
        </form>
      </div>
      <div
        className="tw:hidden tw:lg:flex tw:flex-1 tw:h-full tw:min-h-screen tw:relative tw:overflow-hidden"
        style={{ borderTopLeftRadius: '50%' }}>
        <Image
          src={bruntwoodPhoto}
          alt="Bruntwood-coworking-photo"
          className="tw:object-cover tw:h-full tw:w-full"
          priority
          fill
          sizes="(min-width: 1024px) 50vw, 100vw"
        />
      </div>
    </div>
  );
}
